import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import sanitizer from 'sanitizer';

import Feedback from 'images/zappos/feedback.svg';
import Sort from 'components/search/Sort';
import { retrieveH1TagFromFilters } from 'helpers/SeoOptimizedDataHelper';
import marketplace from 'cfg/marketplace.json';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { SEARCH_PAGE } from 'constants/amethystPageTypes';
import FeatureFeedback from 'components/FeatureFeedback';
import Tooltip from 'components/common/Tooltip';
import useFocusTrap from 'hooks/useFocusTrap';

import css from 'styles/components/search/desktopSearchHeader.scss';

const {
  search: { hasSelectSort, sortOptions, showSearchFeedback }
} = marketplace;

export const DesktopSearchHeader = ({ totalProductCount, onSortSelected, filters, makePersonalizedSortToggle, originalTerm, hydraNewProductCard }, { testId }) => {
  const [ tooltipVisible, setTooltipVisible] = useState(false);

  useEffect(() => {
    // original term Changed
    setTooltipVisible(false);
  }, [originalTerm]);

  const tooltipClick = () => {
    const element = document.getElementById('feedbackRequest');
    if (element.getAttribute('aria-expanded') === 'true') {
      element.setAttribute('aria-expanded', 'false');
    } else {
      element.setAttribute('aria-expanded', 'true');
    }
    setTooltipVisible(tooltipVisible ? false : true);
  };

  const closeFeedback = () => {
    setTooltipVisible(false);
  };

  const handleKeydown = useCallback(e => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeFeedback();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);

  const tooltipRef = useFocusTrap({ shouldFocusFirstElement: true, active: tooltipVisible });

  function makeSearchFeedback() {
    return (
      <div ref={tooltipRef}>
        <Tooltip
          tooltipId="searchFeedback"
          tooltipClassName={css.tooltip}
          contentClassName={css.content}
          mobileWrapper={css.mobileContainer}
          mobileClass={css.mobileEncompasss}
          direction="right"
          clickable={true}
          clickOpen={tooltipVisible}
          content={
          <>
            <header>
              {/* following eslint-disable is for accessibility */}
              <h3 // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                id="feedBackTitle">Let us know your feedback!</h3>
              <button
                type="button"
                value="close"
                className={css.close}
                aria-label="Close"
                onClick={() => closeFeedback()}>
              </button>
            </header>
            <FeatureFeedback
              isYesNoOnly={true}
              autoOpenOnYesNoClick={true}
              completionMessage="Thank you!"
              feedbackQuestion="Did you find what you were looking for?"
              feedbackType="SEARCH_RELEVANCY_FEEDBACK"
              pageType={SEARCH_PAGE}
              source="search"
              wrapperClass={css.feedbackWrapper}
              yesNoWrapperClass={css.feedbackYesNoWrapper}
            />
          </>
          }>
        </Tooltip>
      </div>);
  }
  return (
    <div className={css.desktopSearchHeader}>
      <div className={css.countWrapper}>
        <h1 className={css.fullCount} dangerouslySetInnerHTML={{ __html: sanitizer.sanitize(retrieveH1TagFromFilters(filters)) }} data-test-id={testId('desktopSearchResultsText')}/>
        <span className={css.shortCount} data-test-id={testId('searchResultsItemCount')}>
          {!!totalProductCount && `${totalProductCount} items found`}
        </span>

        {hydraNewProductCard && showSearchFeedback &&
        <section className={css.searchFeedback}>
          <button
            type="button"
            className={css.feedbackButton}
            onClick={tooltipClick}
            aria-expanded="false"
            id="feedbackRequest">
              Have Feedback?
            <img src={Feedback} alt="" />
          </button>
          {makeSearchFeedback()}
        </section>
        }
      </div>

      {hasSelectSort && <span className={css.sortWrapper} data-test-id={testId('desktopSearchSort')}>
        <Sort
          tabIndex={0}
          sortOptions={sortOptions}
          onSortSelected={onSortSelected}
          selectedSort={filters.sort}
          filters={filters}
          testId={testId('selectSortValue')} />
        {makePersonalizedSortToggle && makePersonalizedSortToggle()}
      </span>}
    </div>
  );
};

DesktopSearchHeader.displayName = 'DesktopSearchHeader';
DesktopSearchHeader.contextTypes = {
  testId: PropTypes.func
};

export default withErrorBoundary('DesktopSearchHeader', DesktopSearchHeader);
